import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Button, { BUTTON_STATES } from 'components/shared/button';
import { BUTTON_SUCCESS_TIMEOUT_MS, SUBSCRIBE_FORM_ID, URL_PARAMS } from 'constants/forms';
import MainContext from 'context/main';
import useSegmentAnonId from 'hooks/use-segment-anon-id';
import i18nTranslations from 'i18n';
import SendIcon from 'icons/send.inline.svg';
import getParamsValue from 'utils/get-params-value';
import mutinyIdentify from 'utils/mutiny-identification';
import sendGravityFormData from 'utils/send-gravity-form-data';

import HorizontalLineLeft from './images/horizontal-line-left.inline.svg';
import HorizontalLineRight from './images/horizontal-line-right.inline.svg';
import VerticalLineLeft from './images/vertical-line-left.inline.svg';
import VerticalLineRight from './images/vertical-line-right.inline.svg';

const themes = {
  horizontal: {
    wrapper: 'flex-row p-10 md:py-9 md:px-5',
    title: 'xl:max-w-[180px] lg:max-w-[150px] md:max-w-none',
    form: 'ml-10 lg:ml-8 md:ml-0 md:mt-4 w-[488px] xl:w-[450px] lg:w-[410px] md:w-full',
    circle: 'top-1/2 -right-10 h-[208px] w-[208px] -translate-y-1/2',
  },
  vertical: {
    wrapper: 'flex-col p-8 md:py-9 md:px-5',
    title: '',
    form: 'mx-auto mt-5 w-[592px] lg:mt-4 lg:w-full',
    circle: '-bottom-14 -right-5 h-52 w-52',
  },
  centered: {
    wrapper: '',
    title: '',
    form: 'mx-auto my-10 w-[656px] lg:w-full',
    circle: '',
  },
};

const Subscribe = ({ title, inputPlaceholder, buttonText, orientation, className }) => {
  const [buttonState, setButtonState] = useState(BUTTON_STATES.DEFAULT);
  const {
    language: { locale },
  } = useContext(MainContext);
  const { getSegmentAnonId, SegmentAnonIdInput } = useSegmentAnonId();

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .trim()
      .email(i18nTranslations[locale].form.pleaseProvideValidEmail)
      .required(i18nTranslations[locale].form.isRequiredField),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ resolver: yupResolver(validationSchema) });

  const onSubmit = async ({ email }) => {
    const urlParams = getParamsValue(URL_PARAMS);
    const segmentAnonIdC = getSegmentAnonId();
    setButtonState(BUTTON_STATES.LOADING);

    mutinyIdentify({ email });

    try {
      await sendGravityFormData(SUBSCRIBE_FORM_ID, {
        input_1: email,
        input_2: urlParams.utm_source,
        input_3: urlParams.utm_medium,
        input_4: urlParams.utm_campaign,
        input_5: urlParams.utm_term,
        input_6: urlParams.utm_content,
        input_7: urlParams.gclid,
        input_8: segmentAnonIdC,
      });

      setButtonState(BUTTON_STATES.SUCCESS);

      setTimeout(() => {
        setButtonState(BUTTON_STATES.DEFAULT);
        reset();
      }, BUTTON_SUCCESS_TIMEOUT_MS);
    } catch (error) {
      setButtonState(BUTTON_STATES.DEFAULT);
    }
  };

  return (
    <section
      className={clsx(
        'relative flex items-center justify-center overflow-hidden rounded-2xl bg-gray-1 md:block md:text-center',
        themes[orientation].wrapper,
        className
      )}
    >
      <h2 className={clsx('relative z-10 text-3xl font-bold', themes[orientation].title)}>
        {title}
      </h2>
      <form
        className={clsx('relative z-10', themes[orientation].form)}
        method="post"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="relative w-full">
          <input
            className="remove-autocomplete-styles card-shadow block h-16 w-full appearance-none rounded-full border-gray-3 bg-white pr-48 pl-8 text-lg leading-none text-black placeholder-black outline-none transition-colors duration-200 placeholder:overflow-visible xl:pr-40 md:pr-22 sm:text-[16px] xs:pr-18 xs:pl-4"
            name="email"
            type="email"
            autoComplete="email"
            placeholder={inputPlaceholder}
            {...register('email')}
          />
          {errors?.email?.message && (
            <span className="absolute left-0 -bottom-2 w-full translate-y-full text-center text-sm leading-none text-primary-2 sm:text-xs">
              {errors.email.message}
            </span>
          )}
        </div>
        <SegmentAnonIdInput />
        <Button
          className="absolute top-1/2 right-2.5 -translate-y-1/2 md:right-1.5 md:h-[52px] md:pr-2.5 md:pl-3.5"
          size="sm"
          theme="secondary-2"
          state={buttonState}
        >
          <span className="md:hidden">{buttonText}</span>
          <span className="sr-only hidden md:block">{buttonText}</span>
          <SendIcon className="hidden w-7 md:block" />
        </Button>
      </form>
      <div
        className={clsx('absolute rounded-full bg-primary-1', themes[orientation].circle)}
        aria-hidden
      />
      {orientation === 'horizontal' && (
        <>
          <HorizontalLineLeft
            className="absolute bottom-0 left-0 w-[144px] xl:-left-8 lg:hidden md:hidden"
            aria-hidden
          />
          <HorizontalLineRight className="absolute bottom-0 right-0 h-full md:hidden" aria-hidden />
        </>
      )}
      {orientation === 'vertical' && (
        <>
          <VerticalLineLeft className="absolute left-0 bottom-0 w-[197px] md:hidden" aria-hidden />
          <VerticalLineRight
            className="absolute -right-16 -bottom-4 w-[286px] md:hidden"
            aria-hidden
          />
        </>
      )}
    </section>
  );
};

Subscribe.propTypes = {
  title: PropTypes.string.isRequired,
  inputPlaceholder: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  orientation: PropTypes.string,
  className: PropTypes.string,
};

Subscribe.defaultProps = {
  orientation: 'horizontal',
  className: null,
};

export default Subscribe;
